/**
 * Categorias do Google Maps no idioma da pessoa (GET /api/content/place-categories, coleção
 * `place_categories`, 18/10/2026). Os negócios locais guardam a categoria no idioma em que o
 * Maps respondeu na captura, muitas vezes em inglês; aqui ela vira o nome em pt, es ou fr
 * (os demais idiomas veem inglês).
 *
 * A categoria é reconhecida pelo código (GCID, quando o registro o tem) ou pelo nome em
 * qualquer um dos idiomas dos arquivos do Google. Sem nome no idioma da pessoa, fica o nome
 * capturado (nas capturas do Brasil ele já vem em português) e, por último, o inglês.
 */
import { MONTHLY_TTL_MS, readDaily, writeDaily, type DailyEntry } from './dailyCache';

/** [gcid, nome no idioma ('' quando falta), inglês, ...outros nomes]. */
export type PlaceCategoryRow = [string, string, string, ...string[]];

export interface PlaceCategoriesPayload {
  lang: string;
  version: string | null;
  rows: PlaceCategoryRow[];
}

const CACHE_PREFIX = 'place_categories:v1:';

function isPayload(data: unknown): data is PlaceCategoriesPayload {
  return Boolean(data && Array.isArray((data as PlaceCategoriesPayload).rows));
}

export function readCachedPlaceCategories(
  lang: string,
  now = Date.now(),
): DailyEntry<PlaceCategoriesPayload> | undefined {
  // Um mês no navegador: a lista de categorias do Maps quase não muda.
  return readDaily(`${CACHE_PREFIX}${lang}`, isPayload, now, MONTHLY_TTL_MS);
}

export function writeCachedPlaceCategories(
  lang: string,
  data: PlaceCategoriesPayload,
  now = Date.now(),
): void {
  writeDaily(CACHE_PREFIX, `${CACHE_PREFIX}${lang}`, data, now);
}

export interface PlaceCategoryIndex {
  /** Nome para a tela de uma categoria gravada (nome em qualquer idioma ou GCID). */
  name: (stored: string, id?: string) => string;
  /**
   * GCID de uma categoria gravada só pelo nome (em qualquer idioma) ou já pelo código. Os
   * negócios capturados antes de 18/10/2026 não guardam o `categoryIds`.
   */
  id: (stored: string) => string | undefined;
}

function key(text: string): string {
  return text.trim().toLowerCase();
}

const built = new WeakMap<PlaceCategoriesPayload, PlaceCategoryIndex>();

/** Um índice por resposta: cada cartão usa o mesmo, sem remontar 16 mil nomes. */
export function indexPlaceCategories(payload: PlaceCategoriesPayload): PlaceCategoryIndex {
  const cached = built.get(payload);
  if (cached) return cached;
  const index = buildIndex(payload);
  built.set(payload, index);
  return index;
}

function buildIndex(payload: PlaceCategoriesPayload): PlaceCategoryIndex {
  const byId = new Map<string, PlaceCategoryRow>();
  const byName = new Map<string, PlaceCategoryRow>();
  for (const row of payload.rows) byId.set(row[0], row);
  // Primeiro os nomes principais (idioma e inglês), depois os demais: num nome repetido entre
  // categorias, vale a categoria em que ele é o nome principal.
  for (const row of payload.rows) {
    for (const name of [row[1], row[2]]) if (name && !byName.has(key(name))) byName.set(key(name), row);
  }
  for (const row of payload.rows) {
    for (const name of row.slice(3)) if (name && !byName.has(key(name))) byName.set(key(name), row);
  }
  return {
    name: (stored, id) => {
      const row = (id ? byId.get(id) : undefined) ?? byName.get(key(stored)) ?? byId.get(stored);
      if (!row) return stored;
      return row[1] || stored || row[2];
    },
    id: (stored) => (byName.get(key(stored)) ?? byId.get(stored.trim()))?.[0],
  };
}

/** Lê os GCIDs do `i18n` que a extensão e o backend gravam ({ pt: { categories: [{ id: ['gcid', …] }] } }). */
export function categoryIdsFrom(i18n: unknown): string[] | undefined {
  if (!i18n || typeof i18n !== 'object') return undefined;
  for (const value of Object.values(i18n as Record<string, unknown>)) {
    const categories = (value as { categories?: unknown } | null)?.categories;
    if (!Array.isArray(categories) || categories.length === 0) continue;
    const ids = categories.map((category) => {
      const id = (category as { id?: unknown } | null)?.id;
      const first = Array.isArray(id) ? id[0] : id;
      return typeof first === 'string' ? first : '';
    });
    if (ids.some(Boolean)) return ids;
  }
  return undefined;
}

/** Dicionário em uso, para quem traduz fora de um componente (exportação da lista). */
let active: PlaceCategoryIndex | null = null;

export function setActivePlaceCategories(index: PlaceCategoryIndex | null): void {
  active = index;
}

/** Nomes das categorias de um negócio no idioma da pessoa (como foram gravados, sem dicionário). */
export function translatePlaceCategories(
  place: { categories?: string[]; categoryIds?: string[]; i18n?: Record<string, unknown> } | undefined,
  index: PlaceCategoryIndex | null = active,
): string[] {
  const categories = place?.categories ?? [];
  if (!index || categories.length === 0) return categories;
  const ids = place?.categoryIds ?? categoryIdsFrom(place?.i18n);
  return categories.map((name, i) => index.name(name, ids?.[i]));
}

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

const names = new WeakMap<PlaceCategoriesPayload, { name: string; folded: string }[]>();

/**
 * Nomes das categorias no idioma da pessoa, sem repetir. As que o Google não traz nesse idioma
 * ficam fora da sugestão (pedido do Marcelo, 18/10/2026): nada de categoria em inglês para quem
 * usa o painel em pt, es ou fr. Em inglês, o nome da linha já é o inglês.
 */
function categoryNames(payload: PlaceCategoriesPayload): { name: string; folded: string }[] {
  const cached = names.get(payload);
  if (cached) return cached;
  const seen = new Set<string>();
  const out: { name: string; folded: string }[] = [];
  for (const row of payload.rows) {
    const name = row[1];
    const folded = fold(name);
    if (!name || seen.has(folded)) continue;
    seen.add(folded);
    out.push({ name, folded });
  }
  out.sort((a, b) => a.folded.localeCompare(b.folded));
  names.set(payload, out);
  return out;
}

/**
 * Sugestões para o campo de segmento de Negócios locais: primeiro as categorias que começam
 * pelo texto digitado, depois as que têm uma palavra começando por ele, depois as que o contêm.
 */
export function suggestPlaceCategories(
  payload: PlaceCategoriesPayload | undefined,
  query: string,
  exclude: readonly string[] = [],
  limit = 8,
): string[] {
  const q = fold(query);
  if (!payload || q.length < 2) return [];
  const taken = new Set(exclude.map(fold));
  const starts: string[] = [];
  const word: string[] = [];
  const contains: string[] = [];
  for (const { name, folded } of categoryNames(payload)) {
    if (taken.has(folded)) continue;
    if (folded.startsWith(q)) starts.push(name);
    else if (folded.includes(` ${q}`)) word.push(name);
    else if (folded.includes(q)) contains.push(name);
    if (starts.length >= limit) break;
  }
  return [...starts, ...word, ...contains].slice(0, limit);
}
